/**
 * AI Execution Log Hooks
 * Provides React Query hooks for fetching AI execution history and stats.
 */

import { useQuery } from '@tanstack/react-query';
import api from '../api/client';

const API_BASE = '/planning';

// ============ Types ============

export interface AIExecutionLog {
  id: string;
  scenario: string;
  execution_type: 'calculation' | 'retry' | 'test';
  execution_type_display: string;
  status: 'started' | 'success' | 'failed' | 'timeout';
  status_display: string;
  retry_attempt: number;
  prompt_length: number;
  response_length: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  model: string;
  processing_time_seconds: number;
  stop_reason: string;
  request_id: string;
  celery_task_id: string;
  error_message: string;
  error_type: string;
  created_at: string;
  completed_at: string | null;
  duration_display: string;
  tokens_display: string;
}

export interface AIExecutionLogDetail extends AIExecutionLog {
  prompt_text: string;
  response_text: string;
}

export interface AIExecutionStats {
  total_executions: number;
  total_tokens: number;
  avg_tokens: number;
  avg_latency_seconds: number;
  total_latency_seconds: number;
  failed_count: number;
}

// ============ Query Keys ============

export const aiExecutionKeys = {
  list: (scenarioId: string, filters?: Record<string, string>) =>
    ['ai-executions', scenarioId, filters] as const,
  detail: (scenarioId: string, logId: string) =>
    ['ai-executions', scenarioId, logId] as const,
  stats: (scenarioId: string, days?: number) =>
    ['ai-execution-stats', scenarioId, days] as const,
};

// ============ API Functions ============

async function fetchExecutions(
  scenarioId: string,
  filters?: { execution_type?: string; status?: string },
): Promise<AIExecutionLog[]> {
  const params = new URLSearchParams();
  if (filters?.execution_type) params.set('execution_type', filters.execution_type);
  if (filters?.status) params.set('status', filters.status);

  const url = `${API_BASE}/scenarios/${scenarioId}/ai-executions/${
    params.toString() ? `?${params.toString()}` : ''
  }`;
  const response = await api.get(url);
  return response.data.results || response.data;
}

async function fetchExecutionDetail(
  scenarioId: string,
  logId: string,
): Promise<AIExecutionLogDetail> {
  const response = await api.get(
    `${API_BASE}/scenarios/${scenarioId}/ai-executions/${logId}/`,
  );
  return response.data;
}

async function fetchExecutionStats(
  scenarioId: string,
  days = 30,
): Promise<AIExecutionStats> {
  const response = await api.get(
    `${API_BASE}/scenarios/${scenarioId}/ai-stats/?days=${days}`,
  );
  return response.data;
}

// ============ Hooks ============

/** List AI executions for a scenario */
export function useScenarioExecutions(
  scenarioId: string | undefined,
  filters?: { execution_type?: string; status?: string },
) {
  return useQuery({
    queryKey: aiExecutionKeys.list(scenarioId!, filters),
    queryFn: () => fetchExecutions(scenarioId!, filters),
    enabled: !!scenarioId,
    refetchInterval: 10000, // Poll every 10s to catch running executions
  });
}

/** Get single execution detail (with prompt/response text) */
export function useExecutionDetail(
  scenarioId: string | undefined,
  logId: string | undefined,
) {
  return useQuery({
    queryKey: aiExecutionKeys.detail(scenarioId!, logId!),
    queryFn: () => fetchExecutionDetail(scenarioId!, logId!),
    enabled: !!scenarioId && !!logId,
  });
}

/** Get aggregated execution stats for a scenario */
export function useExecutionStats(
  scenarioId: string | undefined,
  days = 30,
) {
  return useQuery({
    queryKey: aiExecutionKeys.stats(scenarioId!, days),
    queryFn: () => fetchExecutionStats(scenarioId!, days),
    enabled: !!scenarioId,
    staleTime: 30000,
  });
}
